import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import {DataService} from '../../../../Services/data.service';
import { ActivatedRoute, Router } from '@angular/router';
import { BankModel } from '../../../../DataModels/bank-model';
import { CompanyModel } from '../../../../DataModels/company-model';
import {Observable, Subject, take, takeUntil, of} from 'rxjs';
import { AccountModel } from '../../../../DataModels/account-model';
import { CurrencyModel } from '../../../../DataModels/currency-model';
import { CheckStockModel } from '../../../../DataModels/check-stock-model';
import { ConfirmDialogService } from '../../../../Services/confirm-dialog.service';
import { BsToastService } from '../../../../Services/bs-toast-service';
import {Title} from '@angular/platform-browser';
import {NavigationService} from '../../../../Services/navigation.service';
import {DecimalPipe} from '@angular/common';
import {LOCALE_ID, Inject} from '@angular/core';
import {UnsavedChangesComponent} from '../../../unsaved-changes/unsaved-changes/unsaved-changes.component';


@Component({
  selector: 'app-banks-form',
  templateUrl: './bank-form.component.html',
  styleUrls: ['./bank-form.component.css'],
  standalone: false
})
export class BankFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('unsavedChanges') unsavedChangesComponent!: UnsavedChangesComponent;

  originalFormData: any = {};
  currentFormData: any;

  // Add a destroy subject to manage subscriptions
  private destroy$ = new Subject<void>();



  bankID: string;

  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: true,
    saveClose: true,
    print: false,
  };

  bank: BankModel;
  bankForm: UntypedFormGroup;

  companies$: Observable<CompanyModel[]>;
  accounts$: Observable<AccountModel[]>;
  checkStocks$: Observable<CheckStockModel[]>;
  currencies$: Observable<CurrencyModel[]>;

  companyListLength = 1;
  accountListLength = 1;
  currencyListLength = 1;

  accountData: any;
  cashAccountBalance = '';

  itemId = false;
  submitted = false;
  buttonsDisabled = false;
  isSaveNew = false;

  constructor(
    private table: DataService,
    private notification: BsToastService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private confirmDialogService: ConfirmDialogService,
    private titleService: Title,
    private decimalPipe: DecimalPipe,
    private nav: NavigationService,

    @Inject(LOCALE_ID) private locale: string
  ) {
    this.createForm();
    this.bankID = this.route.snapshot.params.id;
  }

  // canDeactivate(): Observable<boolean> {
  //   console.log('canDeactivate');
  //   if (this.unsavedChangesComponent) {
  //     console.log('canDeactivate IF');
  //     return this.unsavedChangesComponent.canDeactivate();
  //   }
  //   return of(true);
  // }

  createForm() {
    this.bankForm = this.formBuilder.group({
      name: new UntypedFormControl(''),
      check_name: new UntypedFormControl(''),
      address_1: new UntypedFormControl(''),
      address_2: new UntypedFormControl(''),
      city: new UntypedFormControl(''),
      state: new UntypedFormControl(''),
      zip: new UntypedFormControl(''),
      phone: new UntypedFormControl(''),
      account_number: new UntypedFormControl(''),
      transit_number: new UntypedFormControl(''),
      institution_number: new UntypedFormControl(''),
      routing_number: new UntypedFormControl(''),
      account_id: new UntypedFormControl(''),
      company_id: new UntypedFormControl(''),
      currency_id: new UntypedFormControl(''),
      entity_id: new UntypedFormControl(''),
      stock_id: new UntypedFormControl(''),
      next_check_number: new UntypedFormControl(''),
      audit_sequence: new UntypedFormControl(0),
      active: new UntypedFormControl('true'),
    });
  }

  ngOnInit() {
    if (this.bankID) {
      this.loadBank();
      if (this.bankID) {
        this.buttonConfig.reset = false;
      }
    } else {
      Promise.resolve().then(() => {
        try {
          this.originalFormData = this.buildSnapshot();
        } catch {
        }
      });
    }
    // Subscribe to form changes
    this.bankForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(formValue => {
        this.currentFormData = this.buildSnapshot();
      });

    this.loadLookups();
  }

  ngAfterViewInit() {
    this.titleService.setTitle($localize`:Bank @@bank:Bank`);
  }

  private loadBank() {
    this.table.getData('bank', this.bankID).subscribe({
      next: (result) => {
        this.bank = result;
        this.originalFormData = structuredClone(this.bank);
        // console.log(this.bank);
        this.getBankAccountBalance();
      },
      error: (error: HttpErrorResponse) => {
        // this.notification.error($localize`:Error Loading Bank @@errorLoadingBank:Error Loading Bank`);
        console.log(error);
      },
      complete: () => {
        this.bankForm.patchValue(this.bank);
        try {
          this.originalFormData = this.buildSnapshot();
        } catch {
        }
      }
    });
  }


  private getBankAccountBalance() {
    // get balance
    const urlParamsJson = {
      fromDate: new Date('12/31/2000').toISOString(),
      toDate: new Date().toISOString(),
      account_id: this.bank.account_id,
      // bank_id: this.vm.request.bank_id,
      // company_id: this.vm.request.company_id,
    };

    const urlParams: string = this.encodeQueryData(urlParamsJson);
    // console.log('params: ' + urlParams);
    this.table.getData('transaction_detail', '?' + urlParams).pipe(take(1))
      .subscribe((result) => {
        this.accountData = result;
        // console.log('bank bal', this.accountData);
        if (this.accountData?.length === 0) {
          this.cashAccountBalance = '0.00';
          return;
        } else if (this.accountData?.length === 1) {
          this.cashAccountBalance = this.decimalPipe.transform(this.accountData[0].endingBalance, '1.2-2', this.locale);
        }
      });
  }

  encodeQueryData(data) {
    const ret = [];
    for (const d in data) {
      if (data[d] != null && data[d] != '') {
        ret.push(encodeURIComponent(d) + '=' + encodeURIComponent(data[d]));
      }
    }
    return ret.join('&');
  }

  loadLookups() {

    this.table.getData('company', '?pageNumber=0&pageSize=999999').subscribe((res) => {
      this.companies$ = of(res.Data || []);
      this.companyListLength = (res.Data || []).length;
      });

    this.table
      .getData('account', '?pageNumber=0&pageSize=999999')
      .subscribe((res) => {
        this.accounts$ = of(res.Data || []);
        this.accountListLength = (res.Data || []).length;
      });

    this.table
      .getData('currency', '?pageNumber=0&pageSize=999999')
      .subscribe((res) => {
        this.currencies$ = of(res.Data || []);
        this.currencyListLength = (res.Data || []).length;
      });

    this.table
      .getData('check_stock', '?pageNumber=0&pageSize=999999')
      .subscribe((res) => {
        this.checkStocks$ = of(res.Data || []);
      });
  }

  get f() {
    return this.bankForm.controls;
  }

  saveClose() {
    if (this.onSave(true)) {
    }
  }

  saveNew() {
    // Clear only after async save succeeds
    this.isSaveNew = true;
    this.onSave(false);
  }

  deleteItem() {
    let retMsg = '';
    const messages: string[] = [
      $localize`:Are you sure you want to delete bank? @@areYouSureYouWantToDeleteBank:Are you sure you want to delete bank?`,
      this.bank.name,
    ];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.table.deleteDataById('bank', this.bankID).subscribe(
          (result) => {
            retMsg = result;
            // this.notification.success($localize`:Deleted @@deleted:Deleted`);
          },
          (error: HttpErrorResponse) => {
            // this.notification.error($localize`:Error @@error:Error`);
            console.log(error);
          },
          () => {
            this.onClose();
          }
        );
      },
      () => {
        this.notification.showSuccessToast(
          $localize`:Delete Canceled @@deleteCanceled:Delete Canceled`
        );
      }
    );
  }

  resetForm() {
    this.resetToDefaults();
    try {
      this.originalFormData = this.buildSnapshot();
    } catch {
    }
  }

  onClose() {
    const fallback = '/admin/bank-list';
    this.nav.backOr(fallback);
  }

  redirectTo(uri: string) {
    this.router
      .navigateByUrl('/home', { skipLocationChange: true })
      .then(() => this.router.navigate([uri]));
  }

  private resetToDefaults(): void {
    // Reset the form to known default values and clear component state for a fresh entry
    this.bankForm.reset({
      name: '',
      check_name: '',
      address_1: '',
      address_2: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      account_number: '',
      transit_number: '',
      institution_number: '',
      routing_number: '',
      account_id: '',
      company_id: '',
      currency_id: '',
      entity_id: '',
      stock_id: '',
      next_check_number: '',
      audit_sequence: 0,
      active: true,
    });
    this.bank = {
      name: '', check_name: '', address_1: '', address_2: '', city: '', state: '', zip: '', phone: '',
      account_number: '', transit_number: '', institution_number: '', routing_number: '', account_id: '',
      company_id: '', currency_id: '', entity_id: '', stock_id: '', next_check_number: '', audit_sequence: 0, active: true
    } as unknown as BankModel;
    this.bankID = null;
    this.submitted = false;
    try {
      this.originalFormData = this.buildSnapshot();
    } catch {
    }
  }

  onSave(saveClose: boolean) {
    this.submitted = true;
    if (this.bankForm.invalid) {
      this.notification.showErrorToast(
        $localize`:Form Errors! @@formErrors:Form Errors!`
      );
      return false;
    }
    this.bank = Object.assign({}, this.bankForm.value);
    if (this.bankID) {
      if (this.bankForm.invalid === false) {
        this.table.patch('bank', this.bankID, this.bank).then(
          async () => {
            try {
              this.originalFormData = this.buildSnapshot();
            } catch {
            }
            if (saveClose) {
              this.onClose();
            } else if (this.isSaveNew) {
              this.isSaveNew = false;
              this.resetToDefaults();
            }
          },
          (error: HttpErrorResponse) => {
            console.log(error);
          }
        ).finally(() => {
          this.submitted = false;
        });
      }
    } else {
      this.table.postWithPromise('bank', this.bank).then((res) => {
        // stops double posting when clicking Save
        let apiReturn: any = null;
        if (typeof res === 'object') {
          apiReturn = JSON.parse(JSON.stringify(res));
        }
        this.bankID = apiReturn.id;
      }).then(() => {
        try {
          this.originalFormData = this.buildSnapshot();
        } catch {
        }
        if (saveClose) {
          this.onClose();
        } else if (this.isSaveNew) {
          this.isSaveNew = false;
          this.resetToDefaults();
        }
      }).catch((error: HttpErrorResponse) => {
        console.log(error);
      }).finally(() => {
        this.submitted = false;
      });
    }
    return true;
  }

  buildSnapshot(): any {
    try {
      return this.bankForm.getRawValue();
    } catch {
      return {};
    }
  }

  hasChanges(): boolean {
    try {
      const current = this.buildSnapshot();
      const original = this.originalFormData || {};
      return JSON.stringify(current) !== JSON.stringify(original);
    } catch {
      return false;
    }
  }

  checkForChanges(): boolean {
    return this.hasChanges();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
